import { useCallback, useEffect, useState, type RefObject } from 'react';

/**
 * Drag a horizontal rail with the pointer, the way the reader expects a shelf
 * of cards to behave — and the reason every rail can now hide its scrollbar.
 *
 * ## Why the scrollbar went, and what had to replace it
 *
 * A native horizontal scrollbar under every shelf is the one piece of chrome
 * on these pages that belongs to the browser rather than to the design system:
 * it draws in the platform's own colours, it ignores both themes, and it
 * repeats once per shelf down the whole page. Hiding it costs nothing on touch
 * — a finger already scrolls the rail — but on a desktop pointer it would have
 * removed the ONLY affordance a reader without a horizontal wheel has. So the
 * scrollbar is hidden and the rail is made draggable in the same change;
 * neither half ships alone.
 *
 * The hover arrows stay. They are the keyboard- and screen-reader-reachable
 * control and they page by a whole screenful; dragging is the direct gesture
 * beside them, not a replacement for them.
 *
 * ## `scrollLeft` arithmetic here is RELATIVE, and that is what makes it
 * direction-safe
 *
 * The rail is `dir="rtl"`, where `scrollLeft` runs from `-max` at the reading
 * end to `0` at the start. Nothing below reads its SIGN: the drag writes
 * `startScroll - dx`, which means «content follows the pointer» in either
 * direction mode, because `scrollLeft` always grows toward visual-right
 * whatever the range is. A version that detected the direction and flipped a
 * sign would be a second thing to keep correct for no gain.
 *
 * ## Three details that each cost a broken rail if omitted
 *
 *   • **Touch is left entirely alone.** A `pointerType === 'touch'` press
 *     returns immediately, so the browser's own panning — momentum, rubber
 *     band, and the vertical scroll that starts inside a rail — is untouched.
 *     Hijacking it is how a carousel ends up trapping the page scroll.
 *   • **Snapping is suspended for the duration of the drag.** Under
 *     `scroll-snap-type: x mandatory` the browser re-snaps after a
 *     programmatic `scrollLeft` write, so the rail fights the pointer and
 *     stutters back to the nearest card. It is restored on release, which is
 *     also what gives the gesture its settle-onto-a-card ending.
 *   • **The click that ends a drag is swallowed.** Every card on these rails
 *     is a link; without this, letting go of a drag navigates to whatever the
 *     pointer happens to be over. A press that never passed the movement
 *     threshold is not a drag and its click goes through untouched.
 */

/**
 * The utilities that take the browser's own scrollbar off a scroller.
 *
 * One token rather than the string repeated at five call sites: both halves
 * are needed (`scrollbar-width` for Firefox and the modern engines, the
 * pseudo-element for the WebKit ones), and a scroller that carried only one of
 * them would hide the bar in some browsers and not others.
 */
export const HIDDEN_SCROLLBAR = '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden';

/** Below this, the press is a click. Above it, the reader is dragging. */
const DRAG_THRESHOLD_PX = 6;

/** Presses that belong to the element under the pointer, never to the rail. */
const NEVER_DRAG = 'input, textarea, select, [contenteditable="true"], [data-no-drag-scroll]';

/**
 * THE HOOK HANDS BACK A CALLBACK REF, AND THAT IS THE WHOLE FIX.
 *
 * It used to take a `RefObject` and attach inside a `useEffect` keyed on that
 * object. A `RefObject` never changes identity, and mutating `.current` is not
 * a render — so the effect ran exactly once, on the mount that followed the
 * FIRST render, and read whatever `.current` held at that moment. Every
 * scroller on this app whose element is absent on the first render therefore
 * never got a listener at all: the home page's tag chip strip returns `null`
 * while its vocabulary is loading, mounted its `<nav>` one render later, and
 * from then on could not be dragged — the strip drew a scrollbar-less overflow
 * with no gesture behind it, which on a desktop pointer is a rail that simply
 * does not move. The rails escaped it only because their parent withholds the
 * component until it has cards.
 *
 * React calls a callback ref with the node on attach and with `null` on
 * detach, so routing it through state makes the element itself the effect's
 * dependency. Mounting late, unmounting, and remounting are then all the same
 * event, and none of them can be missed.
 *
 * `ref` stays accepted and is still populated, because three call sites read
 * `.current` for their own paging and direction arithmetic. It is written
 * here rather than passed to the element, so an element can never be attached
 * to the ref without also being attached to the gesture.
 */
export function useDragScroll<T extends HTMLElement>(ref?: RefObject<T | null>): (node: T | null) => void {
  const [rail, setRail] = useState<T | null>(null);

  const attach = useCallback(
    (node: T | null) => {
      // `RefObject` is read-only in the React 19 types; the write is the whole
      // point of accepting one, so the cast states it rather than hides it.
      if (ref) (ref as { current: T | null }).current = node;
      setRail(node);
    },
    [ref],
  );

  useEffect(() => {
    if (!rail) return;

    // The grab cursor is an affordance for a pointer that can hover. A touch
    // device never sees it, and `matchMedia` is what says which one this is.
    // Guarded because it is one of the handful of browser APIs jsdom does not
    // implement, and a hook that threw there would take every suite with it.
    const fine = typeof window.matchMedia === 'function' ? window.matchMedia('(pointer: fine)') : null;
    const applyCursor = () => {
      rail.style.cursor = fine?.matches ? 'grab' : '';
    };
    applyCursor();
    fine?.addEventListener('change', applyCursor);

    let pointerId: number | null = null;
    let startX = 0;
    let startScroll = 0;
    let dragging = false;
    let moved = false;
    let snapBefore = '';
    let swallowClick = false;

    const stopDrag = () => {
      if (moved) {
        // Capture is best-effort in both directions: the pointer may already be
        // gone, and jsdom implements neither call.
        try {
          if (pointerId !== null && rail.hasPointerCapture?.(pointerId)) {
            rail.releasePointerCapture(pointerId);
          }
        } catch {
          /* the capture is already released — nothing to undo */
        }
        rail.style.scrollSnapType = snapBefore;
        rail.style.userSelect = '';
        applyCursor();
        // The click generated by this pointer's release is still coming.
        swallowClick = true;
      }
      dragging = false;
      moved = false;
      pointerId = null;
    };

    const onPointerDown = (event: PointerEvent) => {
      if (event.pointerType === 'touch' || event.button !== 0) return;
      if ((event.target as Element | null)?.closest(NEVER_DRAG)) return;

      swallowClick = false;
      pointerId = event.pointerId;
      startX = event.clientX;
      startScroll = rail.scrollLeft;
      dragging = true;
      moved = false;
    };

    const onPointerMove = (event: PointerEvent) => {
      if (!dragging || event.pointerId !== pointerId) return;
      const dx = event.clientX - startX;

      if (!moved) {
        if (Math.abs(dx) < DRAG_THRESHOLD_PX) return;
        moved = true;
        try {
          rail.setPointerCapture(pointerId);
        } catch {
          /* no capture available — the drag still tracks while the pointer
             stays over the rail, which is the common case */
        }
        snapBefore = rail.style.scrollSnapType;
        rail.style.scrollSnapType = 'none';
        rail.style.userSelect = 'none';
        rail.style.cursor = 'grabbing';
        // A press that started on a card name would otherwise select it.
        window.getSelection()?.removeAllRanges();
      }

      rail.scrollLeft = startScroll - dx;
      event.preventDefault();
    };

    /**
     * The browser's OWN drag, suppressed — without this the gesture does not
     * work at all on a real pointer.
     *
     * Every card on these rails is an `<a>` wrapped around an `<img>`, and both
     * are natively draggable. Pressing a poster and moving therefore starts an
     * HTML5 image drag, which takes over the pointer: the `pointermove` stream
     * stops, `pointercancel` arrives, and the rail never moves. Synthetic
     * events do not reproduce it and neither does jsdom, so it survived both
     * the unit tests and a scripted check that dispatched events directly at
     * the element — it showed up the first time a real mouse pressed a poster.
     *
     * Suppressed unconditionally rather than only mid-drag: `dragstart` fires
     * once the browser has already decided the gesture is a drag, so a guard
     * reading «are we dragging yet» would be asking after the answer stopped
     * mattering. Inside a horizontally scrolling shelf the scroll gesture owns
     * the press; dragging a poster out to another window is not a thing this
     * surface offers.
     */
    const onDragStart = (event: Event) => {
      event.preventDefault();
    };

    const onClickCapture = (event: MouseEvent) => {
      if (!swallowClick) return;
      swallowClick = false;
      event.preventDefault();
      event.stopPropagation();
    };

    rail.addEventListener('dragstart', onDragStart);
    rail.addEventListener('pointerdown', onPointerDown);
    rail.addEventListener('pointermove', onPointerMove);
    rail.addEventListener('pointerup', stopDrag);
    rail.addEventListener('pointercancel', stopDrag);
    rail.addEventListener('click', onClickCapture, true);

    return () => {
      fine?.removeEventListener('change', applyCursor);
      rail.removeEventListener('dragstart', onDragStart);
      rail.removeEventListener('pointerdown', onPointerDown);
      rail.removeEventListener('pointermove', onPointerMove);
      rail.removeEventListener('pointerup', stopDrag);
      rail.removeEventListener('pointercancel', stopDrag);
      rail.removeEventListener('click', onClickCapture, true);
      rail.style.cursor = '';
      rail.style.userSelect = '';
    };
  }, [rail]);

  return attach;
}
